import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui-kit/badge';
import { ConfirmationModal } from '@/components/core/confirmation-modal/confirmation-modal';
import { Captcha, useCaptcha } from '@/components/core';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { BreakEntryDialog } from '../components/break-entry-dialog/break-entry-dialog';
import {
  useClockIn,
  useClockOut,
  useEndBreak,
  useGetTodayAttendance,
  useHasPendingCorrectionToday,
  useStartBreak,
} from './use-attendance';
import { useAttendanceRuleContext } from './use-attendance-policy';
import {
  breakTypeOf,
  canSelfClockInToday,
  canTakeLunch,
  chargedBreakSeconds,
  findOpenBreak,
  formatTimeOfDay,
  formatWaitTime,
  getBreakEndBlocker,
  getBreakStartBlocker,
  isOpenRecord,
  isPastClockInCutoff,
  officeNetworkBlocker,
  secondsUntilClockOut,
  sessionStartIso,
  validateDayTotals,
  validateEnteredBreaks,
  visibleBreaks,
} from '../services/attendance-rules';
import { BreakTimeDetail, BreakType } from '../types/attendance.types';

export type ClockPhase = 'out' | 'working' | 'break' | 'done';

/** A running duration as "HH:mm:ss" (the stored totals use `formatDuration`, "HH:mm"). */
export const formatElapsed = (totalSeconds: number): string => {
  const seconds = Math.max(0, Math.floor(totalSeconds));
  const hh = String(Math.floor(seconds / 3600)).padStart(2, '0');
  const mm = String(Math.floor((seconds % 3600) / 60)).padStart(2, '0');
  const ss = String(seconds % 60).padStart(2, '0');
  return `${hh}:${mm}:${ss}`;
};

/** A clock-in/out instant as "HH:mm:ss" in the zone the day was recorded in (its `Timezone`). */
export const formatClockTime = (iso?: string, timeZone?: string): string => {
  if (!iso || Number.isNaN(new Date(iso).getTime())) return '--:--:--';
  return formatTimeOfDay(iso, true, timeZone);
};

/** Time already spent in closed `entries`, for the live display (the open break is added
 * separately; a lunch recorded to its 30-minute minimum counts only up to `now`). */
const sumElapsedSeconds = (entries: BreakTimeDetail[], now: Date): number =>
  entries.reduce((total, entry) => {
    const start = new Date(entry.StartTimeUtc).getTime();
    const end = Math.min(new Date(entry.EndTimeUtc ?? '').getTime(), now.getTime());
    if (!Number.isFinite(start) || !Number.isFinite(end)) return total;
    return total + Math.max(0, Math.floor((end - start) / 1000));
  }, 0);

/** The live status (Component Map › Timer card): Working, On break or On lunch break, with a pulsing dot. */
export const ClockStatusBadge = ({
  phase,
  isOnLunchBreak,
}: {
  phase: ClockPhase;
  isOnLunchBreak: boolean;
}) => {
  const { t } = useTranslation();
  if (phase !== 'working' && phase !== 'break') return null;
  return (
    <Badge
      variant="outline"
      className={cn(
        'flex-none gap-1.5 whitespace-nowrap',
        phase === 'break'
          ? 'border-transparent bg-warning-subtle text-warning-subtle-foreground'
          : 'border-transparent bg-success-subtle text-success-subtle-foreground'
      )}
    >
      <span className="size-[7px] rounded-full bg-current motion-safe:animate-pulse" />
      {phase === 'break' ? t(isOnLunchBreak ? 'ON_LUNCH_BREAK' : 'ON_BREAK') : t('WORKING')}
    </Badge>
  );
};

/**
 * Today's attendance timer: the phase, the live worked and break time, which actions the employee's
 * rules offer, the rule messages, and the actions themselves. Shared by the Attendance timer card and
 * the Home hero (agreed 2 Nov 2026), so both always agree. Render `dialogs` once: the missed-break
 * entry and the clock-out confirmation (with its captcha).
 *
 * Every rule comes from the permissions the user holds (see `useAttendancePolicy`), never from their
 * country. The workflows re-check all of them; this only decides what to offer.
 */
export const useAttendanceClock = () => {
  const { t } = useTranslation();
  const [isConfirmingClockOut, setIsConfirmingClockOut] = useState(false);
  const [isBreakEntryOpen, setIsBreakEntryOpen] = useState(false);
  const [pendingBreakDetails, setPendingBreakDetails] = useState<BreakTimeDetail[]>([]);
  const [, forceTick] = useState(0);

  const context = useAttendanceRuleContext();
  const { policy } = context;
  const canUseBreaks = policy.canTakeBreak || canTakeLunch(policy);

  // CAPTCHA_ON_CLOCK_OUT: the clock-out workflow verifies the token with Google before saving.
  const { toast } = useToast();
  const needsCaptcha = Boolean(policy.rules.clockOutCaptcha);
  // The app has no other captcha (sign-in is SSO only), so this key belongs to clock-out alone.
  const captchaSiteKey = import.meta.env.VITE_ATTENDANCE_CAPTCHA_SITE_KEY || '';
  const {
    code: captchaToken,
    captcha,
    reset: resetCaptcha,
  } = useCaptcha({ siteKey: captchaSiteKey, type: 'reCaptcha-v2-checkbox' });

  const { data: attendance, isLoading } = useGetTodayAttendance();

  const { mutateAsync: clockIn, isPending: isClockingIn } = useClockIn();
  const { mutateAsync: clockOut, isPending: isClockingOut } = useClockOut();
  const {
    mutateAsync: startBreak,
    isPending: isStartingBreak,
    variables: startBreakVariables,
  } = useStartBreak();
  const { mutateAsync: endBreak, isPending: isEndingBreak } = useEndBreak();
  const isSavingBreak = isStartingBreak || isEndingBreak;
  // Only the clicked break button spins; the others are just disabled while it runs.
  const startingBreakType = isStartingBreak ? startBreakVariables?.type : undefined;

  const now = new Date();
  const allEntries = attendance?.BreakTimeDetails ?? [];
  // Breaks the employee took; the gaps between sessions (MULTIPLE_SESSIONS) are not breaks.
  const breaks = visibleBreaks(allEntries);
  const gaps = allEntries.filter((entry) => entry.Type === 'ClockOut');
  const hasClockedIn = Boolean(attendance?.StartTimeUtc);
  const isOpen = isOpenRecord(attendance);
  // The WFO office-day and cutoff rules hide Clock In rather than disable it, like `!canClock`,
  // since there's nothing the employee can do about either from this screen.
  const canClockInToday = canSelfClockInToday(context.isWFH, context.wfoDays, now);
  // A correction of today waiting for review: no clock-in until it is reviewed (the workflow refuses).
  const { hasPending: hasPendingCorrectionToday, isLoading: isPendingCheckLoading } =
    useHasPendingCorrectionToday();
  const isPastCutoff = isPastClockInCutoff(policy, now);
  const networkBlocker = officeNetworkBlocker(policy, context.isWFH);
  // The open break (Start with no End yet) is the single source of truth for "currently on
  // break" — it's written the moment a break starts, so a refresh re-reads it from `attendance`.
  const openBreak = findOpenBreak(breaks);
  const isOnBreak = Boolean(openBreak);
  const isOnLunchBreak = Boolean(openBreak && breakTypeOf(openBreak) === 'Lunch');
  const phase: ClockPhase = !hasClockedIn
    ? 'out'
    : !isOpen
      ? 'done'
      : isOnBreak
        ? 'break'
        : 'working';
  const canClockInAgain =
    phase === 'done' &&
    policy.multipleSessions &&
    policy.canClock &&
    !attendance?.IsForceClockOut &&
    canClockInToday &&
    !hasPendingCorrectionToday &&
    !isPastCutoff;

  const clockOutWaitSeconds =
    attendance && isOpen ? secondsUntilClockOut(policy, sessionStartIso(attendance), now) : 0;
  const endBreakBlocker = openBreak ? getBreakEndBlocker(policy, openBreak, now) : null;
  const regularBreakBlocker = attendance
    ? getBreakStartBlocker(policy, 'Regular', attendance, now)
    : null;
  const lunchBreakBlocker = attendance
    ? getBreakStartBlocker(policy, 'Lunch', attendance, now)
    : null;
  // Why the lunch button is missing, when the employee could take lunch at another time today.
  const lunchHint =
    canTakeLunch(policy) &&
    !isOnBreak &&
    (lunchBreakBlocker === 'LUNCH_BREAK_NOT_AVAILABLE_YET' ||
      lunchBreakBlocker === 'LUNCH_BREAK_WINDOW_CLOSED')
      ? lunchBreakBlocker
      : null;

  useEffect(() => {
    if (phase === 'working' || phase === 'break') {
      const id = setInterval(() => forceTick((tick) => tick + 1), 1000);
      return () => clearInterval(id);
    }
    // Before clocking in (or again) nothing needs a per-second tick, but the cutoff rule is
    // time-based: a Clock In button shown at 15:59 must disappear once 16:00 passes.
    if (phase === 'out' || phase === 'done') {
      const id = setInterval(() => forceTick((tick) => tick + 1), 60 * 1000);
      return () => clearInterval(id);
    }
    return undefined;
  }, [phase]);

  const completedBreakSeconds = sumElapsedSeconds(breaks, now);
  const gapSeconds = sumElapsedSeconds(gaps, now);

  const workedSeconds = (() => {
    if (!attendance?.StartTimeUtc) return 0;
    const startMs = new Date(attendance.StartTimeUtc).getTime();
    if (phase === 'done' && attendance.TotalWorkTimeInSecond != null) {
      // The stored total already includes any counted-but-not-taken lunch minimum.
      return attendance.TotalWorkTimeInSecond;
    }
    const untilMs =
      phase === 'break' && openBreak
        ? new Date(openBreak.StartTimeUtc).getTime()
        : phase === 'done' && attendance.EndTimeUtc
          ? new Date(attendance.EndTimeUtc).getTime()
          : now.getTime();
    return Math.max(0, Math.floor((untilMs - startMs) / 1000) - gapSeconds - completedBreakSeconds);
  })();

  const breakSeconds =
    phase === 'break' && openBreak
      ? completedBreakSeconds +
        Math.max(0, Math.floor((now.getTime() - new Date(openBreak.StartTimeUtc).getTime()) / 1000))
      : phase === 'done' && attendance?.TotalBreakTimeInSecond
        ? attendance.TotalBreakTimeInSecond
        : completedBreakSeconds;

  // The mutations' global error handler already shows the toast on a failure.
  const handleClockIn = async () => {
    try {
      await clockIn();
    } catch {
      /* toast shown */
    }
  };

  const handleStartBreak = async (type: BreakType) => {
    try {
      await startBreak({ type });
    } catch {
      /* toast shown */
    }
  };

  const handleEndBreak = async () => {
    try {
      await endBreak();
    } catch {
      /* toast shown */
    }
  };

  // An employee who takes regular breaks but logged none today (forgot to click Take Break/End
  // Break live) gets one chance to enter them before clocking out. Lunch-only holders skip this:
  // their lunch rule decides what is recorded at clock-out.
  const handleClockOutClick = () => {
    if (policy.canTakeBreak && breaks.length === 0) setIsBreakEntryOpen(true);
    else setIsConfirmingClockOut(true);
  };

  /** Checks breaks typed into the missed-break dialog against the same rules clock-out applies,
   * so the dialog can show the problem instead of the clock-out failing after it closes. */
  const validateEnteredBreakList = (entered: BreakTimeDetail[]): string | null => {
    if (!attendance?.StartTimeUtc) return null;
    const clockInAt = new Date(attendance.StartTimeUtc);
    const at = new Date();
    const invalid = validateEnteredBreaks(policy, entered, clockInAt, at);
    if (invalid) return invalid;
    const clockSeconds = (at.getTime() - clockInAt.getTime()) / 1000 - gapSeconds;
    const charged = chargedBreakSeconds(policy, entered);
    return validateDayTotals(policy, clockSeconds - charged, charged, false);
  };

  const handleClockOut = async () => {
    if (!attendance?.StartTimeUtc || isClockingOut) return;
    if (needsCaptcha && !captchaToken) {
      toast({
        variant: 'destructive',
        title: t(captchaSiteKey ? 'CAPTCHA_REQUIRED' : 'CAPTCHA_NOT_CONFIGURED'),
      });
      return;
    }
    try {
      await clockOut({
        enteredBreaks: pendingBreakDetails,
        ...(needsCaptcha ? { captchaToken } : {}),
      });
      setIsConfirmingClockOut(false);
      // Kept on a refusal, so a retry (e.g. after a new captcha) still sends the entered breaks.
      setPendingBreakDetails([]);
    } catch {
      /* toast shown */
    } finally {
      // A token is good for one verification only.
      if (needsCaptcha) resetCaptcha();
    }
  };

  // Until today's record, the employee's rules and the pending-correction check are in, the phase and
  // which buttons apply are unknown: placeholders instead of buttons that would change a moment later.
  const isClockStateLoading = isLoading || context.isLoading || isPendingCheckLoading;

  const showClockIn =
    !hasClockedIn &&
    !context.isLoading &&
    policy.canClock &&
    canClockInToday &&
    !hasPendingCorrectionToday &&
    !isPastCutoff &&
    !networkBlocker;
  const showEndBreak = isOpen && isOnBreak;
  const showTakeBreak = isOpen && !isOnBreak && !regularBreakBlocker;
  const showTakeLunch = isOpen && !isOnBreak && !lunchBreakBlocker;
  // Any running break, lunch included, is ended first (the clock-out workflow refuses otherwise).
  const showClockOut = isOpen && policy.canClock && !isOnBreak && clockOutWaitSeconds === 0;
  const showClockInAgain = canClockInAgain && !networkBlocker;

  const hints = [
    !policy.canClock && phase !== 'done' && t('ATTENDANCE_NO_CLOCK_PERMISSION'),
    policy.canClock && networkBlocker && (phase !== 'done' || canClockInAgain) && t(networkBlocker),
    policy.canClock &&
      !hasClockedIn &&
      !canClockInToday &&
      t('ATTENDANCE_OFFICE_DAY_NO_SELF_CLOCK_IN'),
    policy.canClock &&
      hasPendingCorrectionToday &&
      (!hasClockedIn || (phase === 'done' && policy.multipleSessions)) &&
      t('ATTENDANCE_CORRECTION_PENDING_FOR_TODAY'),
    policy.canClock &&
      !hasClockedIn &&
      canClockInToday &&
      isPastCutoff &&
      t('ATTENDANCE_CLOCK_IN_CUTOFF_PASSED'),
    policy.canClock &&
      phase === 'working' &&
      clockOutWaitSeconds > 0 &&
      t('ATTENDANCE_CLOCK_OUT_AVAILABLE_IN', { time: formatWaitTime(clockOutWaitSeconds) }),
    phase === 'break' && endBreakBlocker && t(endBreakBlocker),
    phase === 'working' && lunchHint && t(lunchHint),
  ].filter((hint): hint is string => Boolean(hint));

  const dialogs = (
    <>
      <BreakEntryDialog
        open={isBreakEntryOpen}
        onOpenChange={setIsBreakEntryOpen}
        day={attendance?.StartTimeUtc ? new Date(attendance.StartTimeUtc) : now}
        validate={validateEnteredBreakList}
        onConfirm={(entered) => {
          setPendingBreakDetails(entered);
          setIsConfirmingClockOut(true);
        }}
        onSkip={() => {
          setPendingBreakDetails([]);
          setIsConfirmingClockOut(true);
        }}
      />

      <ConfirmationModal
        open={isConfirmingClockOut}
        onOpenChange={(open) => {
          setIsConfirmingClockOut(open);
          if (!open) setPendingBreakDetails([]);
          // The widget unmounts with the dialog; drop its token so a reopen asks for a fresh one.
          if (!open && needsCaptcha) resetCaptcha();
        }}
        title={t('CLOCK_OUT')}
        description={
          needsCaptcha ? (
            <span className="flex flex-col gap-3">
              <span>{t('CLOCK_OUT_CONFIRM_DESCRIPTION')}</span>
              {captchaSiteKey ? (
                <Captcha {...captcha} theme="light" size="normal" />
              ) : (
                <span className="text-destructive">{t('CAPTCHA_NOT_CONFIGURED')}</span>
              )}
            </span>
          ) : (
            t('CLOCK_OUT_CONFIRM_DESCRIPTION')
          )
        }
        onConfirm={handleClockOut}
        confirmLoading={isClockingOut}
        preventAutoClose
        confirmText="CLOCK_OUT"
        confirmClassName="bg-destructive text-destructive-foreground hover:bg-destructive-hover"
      />
    </>
  );

  return {
    attendance,
    isLoading,
    isClockStateLoading,
    phase,
    isOnLunchBreak,
    canUseBreaks,
    workedSeconds,
    breakSeconds,
    hints,
    show: {
      clockIn: showClockIn,
      clockInAgain: showClockInAgain,
      endBreak: showEndBreak,
      takeBreak: showTakeBreak,
      takeLunch: showTakeLunch,
      clockOut: showClockOut,
    },
    busy: {
      clockingIn: isClockingIn,
      clockingOut: isClockingOut,
      savingBreak: isSavingBreak,
      endingBreak: isEndingBreak,
      startingBreakType,
    },
    endBreakBlocker,
    actions: {
      clockIn: handleClockIn,
      startBreak: handleStartBreak,
      endBreak: handleEndBreak,
      clockOut: handleClockOutClick,
    },
    dialogs,
  };
};

export type AttendanceClock = ReturnType<typeof useAttendanceClock>;
